import {
  AmenityHoursWindow,
  AmenityReturn,
  AmenitySeasonReturn,
  AvailabilityNight,
  AvailabilityStart,
  ReservationFeeChange,
  ReservationMoveFeeOutcome,
  ReservationReturn,
} from '../clients/rust-api-client';
import { compareMoney, formatMoney, sumMoney } from '../utils/billingHelpers';

/**
 * Times for amenity bookings, as the association's wall clock.
 *
 * The backend stores and sends a booking's `*_local` fields as the
 * association's local time with no zone attached — `2026-10-03T14:00:00`
 * means 2 pm *at the clubhouse*, whatever zone the phone is in (#641).
 * So none of these helpers goes through `new Date(string)`, which would
 * read that as the browser's own zone and quietly shift it for anybody
 * booking from out of town. Arithmetic is done on the parts with
 * `Date.UTC`, which has no zone to shift by.
 */

export interface LocalParts {
  year: number;
  month: number; // 1–12
  day: number;
  hour: number;
  minute: number;
}

const LOCAL_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/;

const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/** Split a local date or date-time string into its parts. */
export const parseLocal = (value?: string | null): LocalParts | null => {
  if (!value) return null;
  const m = LOCAL_PATTERN.exec(value.trim());
  if (!m) return null;
  return {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hour: m[4] ? Number(m[4]) : 0,
    minute: m[5] ? Number(m[5]) : 0,
  };
};

const toUtcMillis = (p: LocalParts): number =>
  Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);

const fromUtcMillis = (ms: number): LocalParts => {
  const d = new Date(ms);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
  };
};

/** `YYYY-MM-DDTHH:MM:00`, the shape the backend reads a local time in. */
export const toLocalString = (p: LocalParts): string =>
  `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}:00`;

/** `YYYY-MM-DD`. */
export const toLocalDate = (p: LocalParts): string =>
  `${p.year}-${pad(p.month)}-${pad(p.day)}`;

/** A local time `minutes` later, on the same wall clock. */
export const addMinutesLocal = (value: string, minutes: number): string => {
  const p = parseLocal(value);
  if (!p) return value;
  return toLocalString(fromUtcMillis(toUtcMillis(p) + minutes * 60000));
};

/** A local date `days` later. */
export const addDaysToDate = (date: string, days: number): string => {
  const p = parseLocal(date);
  if (!p) return date;
  return toLocalDate(fromUtcMillis(toUtcMillis(p) + days * 86400000));
};

/** Whole minutes from `a` to `b`, both local. */
export const minutesBetween = (a: string, b: string): number => {
  const pa = parseLocal(a);
  const pb = parseLocal(b);
  if (!pa || !pb) return 0;
  return Math.round((toUtcMillis(pb) - toUtcMillis(pa)) / 60000);
};

/** Negative, zero or positive, like a comparator. */
export const compareLocal = (a: string, b: string): number => {
  const pa = parseLocal(a);
  const pb = parseLocal(b);
  if (!pa || !pb) return 0;
  return toUtcMillis(pa) - toUtcMillis(pb);
};

/**
 * The association's wall clock right now, as a local string. Uses the
 * zone the backend reported with the booking, not the browser's.
 */
export const nowLocalIn = (timeZone: string, now: Date = new Date()): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now);
    const get = (type: string): number =>
      Number(parts.find((p) => p.type === type)?.value ?? '0');
    return toLocalString({
      year: get('year'),
      month: get('month'),
      day: get('day'),
      // Some engines report midnight as 24 under h23; it is 0.
      hour: get('hour') % 24,
      minute: get('minute'),
    });
  } catch {
    // An unknown zone name. The browser's clock is the best left.
    return toLocalString(fromUtcMillis(now.getTime() - now.getTimezoneOffset() * 60000));
  }
};

/** Today's date at the association. */
export const todayIn = (timeZone: string, now: Date = new Date()): string =>
  nowLocalIn(timeZone, now).slice(0, 10);

const WEEKDAYS_LONG = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** 0 = Sunday, matching the backend's `amenity_hours.weekday`. */
export const weekdayName = (weekday: number): string =>
  WEEKDAYS_LONG[((weekday % 7) + 7) % 7];

const weekdayOf = (p: LocalParts): number =>
  new Date(toUtcMillis(p)).getUTCDay();

/** `2:00 PM`, from minutes after local midnight. 1440 is `midnight`. */
export const formatClock = (minutes: number): string => {
  if (minutes === 0 || minutes === 1440) return 'midnight';
  if (minutes === 720) return 'noon';
  const h24 = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  const suffix = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${pad(m)} ${suffix}`;
};

/** `2:00 PM` from a local date-time string. */
export const formatLocalTime = (value?: string | null): string => {
  const p = parseLocal(value);
  if (!p) return '';
  const h12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${h12}:${pad(p.minute)} ${p.hour < 12 ? 'AM' : 'PM'}`;
};

/** `Sat, Oct 3, 2026` from a local date or date-time string. */
export const formatLocalDate = (
  value?: string | null,
  opts: { withYear?: boolean } = {}
): string => {
  const p = parseLocal(value);
  if (!p) return '';
  const base = `${WEEKDAYS_SHORT[weekdayOf(p)]}, ${MONTHS_SHORT[p.month - 1]} ${p.day}`;
  return opts.withYear === false ? base : `${base}, ${p.year}`;
};

/** `Sat, Oct 3, 2026 at 2:00 PM`. */
export const formatLocalDateTime = (value?: string | null): string => {
  if (!parseLocal(value)) return '';
  return `${formatLocalDate(value)} at ${formatLocalTime(value)}`;
};

/**
 * `Sat, Oct 3, 2026 · 2:00 PM – 6:00 PM`, or both dates in full when a
 * booking runs past midnight.
 */
export const formatLocalRange = (start: string, end: string): string => {
  const s = parseLocal(start);
  const e = parseLocal(end);
  if (!s || !e) return '';
  const sameDay = s.year === e.year && s.month === e.month && s.day === e.day;
  // An end at exactly midnight belongs to the day it closes.
  const endsAtMidnight =
    e.hour === 0 && e.minute === 0 && minutesBetween(start, end) <= 1440;
  if (sameDay || endsAtMidnight) {
    const endLabel = endsAtMidnight && !sameDay ? 'midnight' : formatLocalTime(end);
    return `${formatLocalDate(start)} · ${formatLocalTime(start)} – ${endLabel}`;
  }
  return `${formatLocalDateTime(start)} – ${formatLocalDateTime(end)}`;
};

/** `1 hour 30 minutes`. */
export const formatDuration = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hours = h === 1 ? '1 hour' : `${h} hours`;
  const mins = m === 1 ? '1 minute' : `${m} minutes`;
  if (h === 0) return mins;
  if (m === 0) return hours;
  return `${hours} ${mins}`;
};

/** `48 hours`, or `2 days` when it divides evenly. */
export const formatHoursSpan = (hours: number): string => {
  if (hours >= 48 && hours % 24 === 0) return `${hours / 24} days`;
  if (hours === 24) return '24 hours';
  return hours === 1 ? '1 hour' : `${hours} hours`;
};

/** `Times are in America/Chicago` — said wherever a booking time is picked. */
export const timesAreIn = (timeZone: string): string =>
  `Times are in ${timeZone}`;

export interface HoursLine {
  weekday: number;
  label: string;
  text: string;
}

/**
 * A week of opening hours, one line per day, Sunday first. A day with no
 * window reads `Closed`; with no hours configured at all the amenity is
 * open around the clock and the caller should say so instead.
 */
export const hoursByDay = (windows: AmenityHoursWindow[]): HoursLine[] =>
  [0, 1, 2, 3, 4, 5, 6].map((weekday) => {
    const today = windows
      .filter((w) => w.weekday === weekday)
      .sort((a, b) => a.open_minute - b.open_minute);
    const text = today.length
      ? today
          .map((w) =>
            w.open_minute === 0 && w.close_minute === 1440
              ? 'Open all day'
              : `${formatClock(w.open_minute)} – ${formatClock(w.close_minute)}`
          )
          .join(', ')
      : 'Closed';
    return { weekday, label: weekdayName(weekday), text };
  });

// ---------------------------------------------------------------------------
// Money and policy, in words
// ---------------------------------------------------------------------------

/** The booking modes a homeowner can book online (#640 phases 1–2). */
export const isBookableMode = (mode: string): boolean =>
  mode === 'exclusive' || mode === 'shared_capacity' || mode === 'overnight';

/** No money: absent, zero or (never expected) negative. */
export const isFreeAmount = (amount?: string | null): boolean =>
  !amount || compareMoney(amount, '0') <= 0;

const isFree = isFreeAmount;

/** `Free`, `$150.00 per booking`, `$20.00 an hour`. */
export const feeSummary = (amenity: Pick<AmenityReturn, 'fee_type' | 'fee_amount'>): string => {
  if (amenity.fee_type === 'flat' && !isFree(amenity.fee_amount)) {
    return `${formatMoney(amenity.fee_amount)} per booking`;
  }
  if (amenity.fee_type === 'hourly' && !isFree(amenity.fee_amount)) {
    return `${formatMoney(amenity.fee_amount)} an hour`;
  }
  if (amenity.fee_type === 'per_night' && !isFree(amenity.fee_amount)) {
    return `${formatMoney(amenity.fee_amount)} a night`;
  }
  return 'Free';
};

export const amenityHasFee = (
  amenity: Pick<AmenityReturn, 'fee_type' | 'fee_amount'>
): boolean => amenity.fee_type !== 'none' && !isFree(amenity.fee_amount);

/**
 * The cancellation policy, as a homeowner needs to hear it before they
 * book. Mirrors the backend: before `cancel_deadline_hours` the fee is
 * reversed; after it, kept. No deadline means reversed up to the start.
 */
export const cancellationPolicy = (
  amenity: Pick<AmenityReturn, 'fee_type' | 'fee_amount' | 'cancel_deadline_hours'> &
    Partial<
      Pick<AmenityReturn, 'booking_mode' | 'late_cancel_policy' | 'late_cancel_amount'>
    >
): string => {
  const what = amenity.booking_mode === 'overnight' ? 'stay' : 'booking';
  if (!amenityHasFee(amenity)) {
    return `Free to cancel any time before your ${what} starts.`;
  }
  const h = amenity.cancel_deadline_hours;
  if (h === null || h === undefined || h <= 0) {
    return `Cancel any time before your ${what} starts and the fee is reversed.`;
  }
  return `Free cancellation until ${formatHoursSpan(h)} before your ${what}. ${lateCancelSentence(amenity)}`;
};

/**
 * What a cancellation after the deadline costs, by the amenity's late
 * policy (#640 phase 2): the whole fee, a flat charge, or a share of it —
 * never more than the fee itself, which the backend also caps.
 */
export const lateCancelSentence = (
  amenity: Partial<Pick<AmenityReturn, 'late_cancel_policy' | 'late_cancel_amount'>>
): string => {
  const amount = amenity.late_cancel_amount;
  if (amenity.late_cancel_policy === 'flat' && amount && !isFree(amount)) {
    return `After that, a ${formatMoney(amount)} late-cancellation charge applies instead of the full fee.`;
  }
  if (amenity.late_cancel_policy === 'percent' && amount && !isFree(amount)) {
    return `After that, ${trimDecimal(amount)}% of the fee is charged for cancelling late.`;
  }
  return 'After that, the fee is not refunded.';
};

/** `25`, `12.5` — a decimal string without trailing zeros. */
const trimDecimal = (amount: string): string =>
  amount.includes('.') ? amount.replace(/\.?0+$/, '') : amount;

/**
 * The amenity's booking rules as short sentences — what the rule engine
 * will hold a homeowner to, said before they run into it.
 */
export const bookingRules = (a: AmenityReturn): string[] => {
  const rules: string[] = [];
  if (a.booking_mode === 'overnight') {
    rules.push(...stayRules(a));
  } else if (a.min_duration_minutes === a.max_duration_minutes) {
    rules.push(`Bookings are ${formatDuration(a.min_duration_minutes)}.`);
  } else {
    rules.push(
      `Bookings run from ${formatDuration(a.min_duration_minutes)} to ${formatDuration(a.max_duration_minutes)}.`
    );
  }
  if (a.booking_mode === 'shared_capacity' && a.capacity) {
    rules.push(`Shared with other households — up to ${a.capacity} people at a time.`);
  }
  if (a.min_notice_hours) {
    rules.push(`Book at least ${formatHoursSpan(a.min_notice_hours)} ahead.`);
  }
  if (a.max_lead_days) {
    rules.push(
      `Book up to ${a.max_lead_days === 1 ? '1 day' : `${a.max_lead_days} days`} ahead.`
    );
  }
  if (a.max_party_size) {
    rules.push(`Up to ${a.max_party_size} people.`);
  }
  const perHousehold = (n: number | null | undefined, span: string): void => {
    if (n) rules.push(`${n} ${n === 1 ? 'booking' : 'bookings'} ${span} per household.`);
  };
  perHousehold(a.per_home_daily_limit, 'a day');
  perHousehold(a.per_home_weekly_limit, 'a week');
  perHousehold(a.per_home_monthly_limit, 'a month');
  if (a.booking_mode !== 'overnight') {
    const hours = (limit: string | null | undefined, span: string): void => {
      if (limit && !isFree(limit)) {
        rules.push(`Up to ${formatHoursAmount(limit)} ${span} per household.`);
      }
    };
    hours(a.per_home_weekly_hours_limit, 'a week');
    hours(a.per_home_monthly_hours_limit, 'a month');
  }
  if (a.per_home_active_limit) {
    rules.push(
      `A household can hold ${a.per_home_active_limit} upcoming ${a.per_home_active_limit === 1 ? 'booking' : 'bookings'} at a time.`
    );
  }
  if (a.requires_vehicle) {
    rules.push("You'll need the vehicle's licence plate to book.");
  }
  if (a.eligibility === 'owners_only') {
    rules.push('Owners only.');
  }
  if (a.approval_mode === 'manual') {
    rules.push('Requests are approved by your management company.');
  }
  return rules;
};

/** `4 hours`, `1.5 hours`, `1 hour` from a decimal-hours string. */
export const formatHoursAmount = (hours: string): string => {
  const trimmed = hours.includes('.') ? hours.replace(/\.?0+$/, '') : hours;
  return trimmed === '1' ? '1 hour' : `${trimmed} hours`;
};

// ---------------------------------------------------------------------------
// Overnight stays (#640 phase 2)
// ---------------------------------------------------------------------------

/** `1 night`, `3 nights`. */
export const nightsLabel = (n: number): string => (n === 1 ? '1 night' : `${n} nights`);

/** `Check in from 3:00 PM, check out by 11:00 AM.` and the night limits. */
export const stayRules = (
  a: Pick<AmenityReturn, 'check_in_minute' | 'check_out_minute' | 'min_nights' | 'max_nights'>
): string[] => {
  const rules: string[] = [];
  if (
    a.check_in_minute !== null &&
    a.check_in_minute !== undefined &&
    a.check_out_minute !== null &&
    a.check_out_minute !== undefined
  ) {
    rules.push(
      `Check in from ${formatClock(a.check_in_minute)}, check out by ${formatClock(a.check_out_minute)}.`
    );
  }
  const min = a.min_nights ?? 1;
  const max = a.max_nights;
  if (max && max === min) {
    rules.push(`Stays are ${nightsLabel(min)}.`);
  } else if (max) {
    rules.push(`Stays run from ${nightsLabel(min)} to ${nightsLabel(max)}.`);
  } else if (min > 1) {
    rules.push(`Stays are at least ${nightsLabel(min)}.`);
  }
  return rules;
};

/** `YYYY-MM-DDTHH:MM:00`, `minute` minutes after local midnight on `date`. */
export const atMinute = (date: string, minute: number): string => {
  const p = parseLocal(date);
  if (!p) return date;
  return addMinutesLocal(toLocalString({ ...p, hour: 0, minute: 0 }), minute);
};

/**
 * A stay's check-in and check-out, as the backend wants them: check-in on
 * the arrival date, check-out `nights` days later. Null when the amenity
 * has no check-in/out times configured.
 */
export const stayTimes = (
  a: Pick<AmenityReturn, 'check_in_minute' | 'check_out_minute'>,
  arrival: string,
  nights: number
): { start: string; end: string } | null => {
  if (a.check_in_minute === null || a.check_in_minute === undefined) return null;
  if (a.check_out_minute === null || a.check_out_minute === undefined) return null;
  return {
    start: atMinute(arrival, a.check_in_minute),
    end: atMinute(addDaysToDate(arrival, nights), a.check_out_minute),
  };
};

/** Whole nights from check-in to check-out, by date. */
export const nightsBetween = (start: string, end: string): number => {
  const s = parseLocal(start);
  const e = parseLocal(end);
  if (!s || !e) return 0;
  const days = (Date.UTC(e.year, e.month - 1, e.day) - Date.UTC(s.year, s.month - 1, s.day)) / 86400000;
  return Math.max(0, Math.round(days));
};

/**
 * How many nights in a row from `arrival` are free, up to `max`: the
 * longest stay that could start that day. 0 when the arrival night itself
 * is taken.
 */
export const freeNightsFrom = (
  nights: Pick<AvailabilityNight, 'date' | 'status'>[],
  arrival: string,
  max: number
): number => {
  const byDate = new Map(nights.map((n) => [n.date, n.status]));
  let count = 0;
  while (count < max && byDate.get(addDaysToDate(arrival, count)) === 'available') {
    count += 1;
  }
  return count;
};

/** The sum of the night prices for a stay, exactly. */
export const stayFee = (
  nights: Pick<AvailabilityNight, 'date' | 'fee'>[],
  arrival: string,
  count: number
): string => {
  const byDate = new Map(nights.map((n) => [n.date, n.fee]));
  const fees: (string | undefined)[] = [];
  for (let i = 0; i < count; i += 1) fees.push(byDate.get(addDaysToDate(arrival, i)));
  return sumMoney(fees);
};

/** Why no stay can begin on `arrival`, in words. */
export const arrivalUnavailableReason = (
  night: Pick<AvailabilityNight, 'status'> | undefined
): string => {
  switch (night?.status) {
    case 'booked':
      return "That night's already booked. Try another arrival date.";
    case 'closed':
      return 'Closed that night.';
    case 'out_of_season':
      return 'Closed for the season.';
    case 'too_soon':
    case 'past':
      return "It's too late to arrive that day.";
    case 'too_far':
      return "That's too far ahead to book yet.";
    default:
      return 'No stay can start that day.';
  }
};

// ---------------------------------------------------------------------------
// Shared capacity (#640 phase 2)
// ---------------------------------------------------------------------------

/** `12 of 20 spots left`, `1 spot left`, `Full`. */
export const spotsLeftText = (
  remaining: number | null | undefined,
  capacity?: number | null
): string => {
  if (remaining === null || remaining === undefined) return '';
  if (remaining <= 0) return 'Full';
  if (capacity) return `${remaining} of ${capacity} ${capacity === 1 ? 'spot' : 'spots'} left`;
  return remaining === 1 ? '1 spot left' : `${remaining} spots left`;
};

/**
 * The fewest spots left at any slot the booking would cover — what the
 * party has to fit into. Null when the grid does not report capacity.
 */
export const remainingOver = (
  starts: Pick<AvailabilityStart, 'start_local' | 'remaining_capacity'>[],
  start: string,
  end: string
): number | null => {
  let min: number | null = null;
  for (const s of starts) {
    if (compareLocal(s.start_local, start) < 0 || compareLocal(s.start_local, end) >= 0) continue;
    if (s.remaining_capacity === null || s.remaining_capacity === undefined) continue;
    min = min === null ? s.remaining_capacity : Math.min(min, s.remaining_capacity);
  }
  return min;
};

// ---------------------------------------------------------------------------
// Seasons (#640 phase 2)
// ---------------------------------------------------------------------------

/** `May 25`. */
export const formatMonthDay = (month: number, day: number): string =>
  `${MONTHS_SHORT[(((month - 1) % 12) + 12) % 12]} ${day}`;

type SeasonDates = Pick<
  AmenitySeasonReturn,
  'name' | 'start_month' | 'start_day' | 'end_month' | 'end_day'
>;

/** `Summer: May 25 – Sep 7`. */
export const seasonLabel = (s: SeasonDates): string =>
  `${s.name}: ${formatMonthDay(s.start_month, s.start_day)} – ${formatMonthDay(s.end_month, s.end_day)}`;

/**
 * The season `date` falls in, if any. A season whose end is before its
 * start wraps the new year (Nov 15 – Mar 15).
 */
export const seasonOn = <S extends SeasonDates>(seasons: S[], date: string): S | null => {
  const p = parseLocal(date);
  if (!p) return null;
  const md = p.month * 100 + p.day;
  return (
    seasons.find((s) => {
      const from = s.start_month * 100 + s.start_day;
      const to = s.end_month * 100 + s.end_day;
      return from <= to ? md >= from && md <= to : md >= from || md <= to;
    }) ?? null
  );
};

/**
 * The opening windows that apply on `date`, mirroring the backend's
 * `day_hours`: in a season, its windows (or the default ones if it has
 * none); outside every season, the default ones — and with seasons but
 * no default windows, none at all: closed for the season.
 */
export const windowsOn = (
  a: Pick<AmenityReturn, 'hours'> & Partial<Pick<AmenityReturn, 'seasons'>>,
  date: string
): { windows: AmenityHoursWindow[]; outOfSeason: boolean } => {
  const seasons = a.seasons ?? [];
  const defaults = a.hours.filter((w) => !w.season_id);
  if (seasons.length === 0) return { windows: a.hours, outOfSeason: false };
  const season = seasonOn(seasons, date);
  if (season) {
    const own = a.hours.filter((w) => w.season_id === season.id);
    return { windows: own.length ? own : defaults, outOfSeason: false };
  }
  if (defaults.length === 0) return { windows: [], outOfSeason: true };
  return { windows: defaults, outOfSeason: false };
};

export interface HoursSection {
  title: string | null;
  lines: HoursLine[];
  /** True when there are no windows at all: open round the clock. */
  allDay: boolean;
}

/**
 * Opening hours grouped for the detail page: one week when there are no
 * seasons; otherwise each season's week, then the rest of the year's (or
 * "closed" when the amenity only opens in season).
 */
export const hoursSections = (
  a: Pick<AmenityReturn, 'hours'> & Partial<Pick<AmenityReturn, 'seasons'>>
): HoursSection[] => {
  const seasons = a.seasons ?? [];
  const defaults = a.hours.filter((w) => !w.season_id);
  if (seasons.length === 0) {
    return [{ title: null, lines: hoursByDay(a.hours), allDay: a.hours.length === 0 }];
  }
  const out: HoursSection[] = seasons.map((s) => {
    const own = a.hours.filter((w) => w.season_id === s.id);
    const windows = own.length ? own : defaults;
    return { title: seasonLabel(s), lines: hoursByDay(windows), allDay: windows.length === 0 };
  });
  if (defaults.length > 0) {
    out.push({ title: 'Rest of the year', lines: hoursByDay(defaults), allDay: false });
  }
  return out;
};

/** `Closed for the season — opens May 25`, when every season is out. */
export const outOfSeasonText = (
  a: Partial<Pick<AmenityReturn, 'seasons'>>,
  today: string
): string => {
  const seasons = a.seasons ?? [];
  const p = parseLocal(today);
  if (!p || seasons.length === 0) return 'Closed for the season.';
  const md = p.month * 100 + p.day;
  const starts = seasons
    .map((s) => ({ s, at: s.start_month * 100 + s.start_day }))
    .sort((x, y) => x.at - y.at);
  const next = starts.find((x) => x.at > md) ?? starts[0];
  return `Closed for the season — opens ${formatMonthDay(next.s.start_month, next.s.start_day)}.`;
};

// ---------------------------------------------------------------------------
// A booking's status and fee, in words
// ---------------------------------------------------------------------------

export type BadgeColor = 'amber' | 'green' | 'red' | 'zinc' | 'sky';

export const statusLabel = (status: string): string => {
  switch (status) {
    case 'pending':
      return 'Awaiting approval';
    case 'approved':
      return 'Confirmed';
    case 'denied':
      return 'Declined';
    case 'cancelled':
      return 'Cancelled';
    case 'expired':
      return 'Expired';
    case 'completed':
      return 'Completed';
    case 'no_show':
      return 'No-show';
    default:
      return status;
  }
};

export const statusColor = (status: string): BadgeColor => {
  switch (status) {
    case 'pending':
      return 'amber';
    case 'approved':
      return 'green';
    case 'denied':
      return 'red';
    case 'completed':
      return 'sky';
    case 'no_show':
      return 'red';
    default:
      return 'zinc';
  }
};

/** What has happened to this booking's fee, from the homeowner's side. */
export const feeStatusText = (
  r: Pick<ReservationReturn, 'fee_amount' | 'fee_status' | 'status'>
): string => {
  const amount = formatMoney(r.fee_amount);
  if (isFree(r.fee_amount)) return 'Free';
  switch (r.fee_status) {
    case 'posted':
      return `${amount} charged to your account`;
    case 'reversed':
      return `${amount} reversed`;
    case 'kept':
      return `${amount} kept — cancelled after the deadline`;
    case 'refund_due':
      return `${amount} — your management company is arranging a refund`;
    case 'credited':
      return `${amount} returned to your account as a credit`;
    case 'refunded':
      return `${amount} returned to you — your management company is sending a refund`;
    case 'late_fee':
      return `${amount} reversed — a late-cancellation charge was added instead`;
    default:
      return r.status === 'pending'
        ? `${amount}, charged only if approved`
        : `${amount} — not charged`;
  }
};

export type CancelOutcomeKind =
  | 'withdraw'
  | 'free'
  | 'reversed'
  | 'kept'
  | 'late_fee'
  | 'returned';

export interface CancelOutcome {
  kind: CancelOutcomeKind;
  message: string;
}

/**
 * What cancelling right now will do to the fee — said *before* the
 * homeowner confirms, never discovered on their statement afterwards.
 *
 * The server says it on every read (`cancel_outcome_now`, from the same
 * rule the cancel itself follows), and its word wins when present. The
 * local reckoning below is the fallback for a read without it.
 * `now` is the association's local wall clock.
 */
export const cancelOutcome = (
  r: Pick<
    ReservationReturn,
    'status' | 'fee_amount' | 'fee_status' | 'fee_refundable_until_local'
  > &
    Partial<Pick<ReservationReturn, 'cancel_outcome_now'>>,
  now: string
): CancelOutcome => {
  if (r.status === 'pending') {
    return {
      kind: 'withdraw',
      message: 'Withdrawing a request is free — nothing has been charged.',
    };
  }
  const amount = formatMoney(r.fee_amount);
  const server = r.cancel_outcome_now;
  if (server) {
    switch (server.outcome) {
      case 'free':
        return { kind: 'free', message: 'This booking is free to cancel.' };
      case 'reversed':
        return { kind: 'reversed', message: `Your ${amount} fee will be reversed.` };
      case 'kept':
        return {
          kind: 'kept',
          message: `It's past the cancellation deadline, so the ${amount} fee will not be refunded.`,
        };
      case 'late_fee':
        return {
          kind: 'late_fee',
          message: `It's past the cancellation deadline. Your ${amount} fee will be reversed and a ${formatMoney(server.charge)} late-cancellation charge added instead.`,
        };
      case 'returned':
        return {
          kind: 'returned',
          message: `You've already paid the ${amount} fee, so it will be returned to you.`,
        };
      default:
        break;
    }
  }
  if (isFree(r.fee_amount) || r.fee_status === 'none') {
    return { kind: 'free', message: 'This booking is free to cancel.' };
  }
  const until = r.fee_refundable_until_local;
  if (!until || compareLocal(now, until) < 0) {
    return {
      kind: 'reversed',
      message: `Your ${amount} fee will be reversed. If you have already paid it, your management company will arrange the refund.`,
    };
  }
  return {
    kind: 'kept',
    message: `It's past the cancellation deadline, so the ${amount} fee will not be refunded.`,
  };
};

/** Whether the outcome costs the household money — shown as a warning. */
export const outcomeCosts = (kind: CancelOutcomeKind): boolean =>
  kind === 'kept' || kind === 'late_fee';

/** Whether the homeowner can still cancel: active, and not yet started. */
export const canCancel = (
  r: Pick<ReservationReturn, 'status' | 'start_local'>,
  now: string
): boolean =>
  (r.status === 'pending' || r.status === 'approved') &&
  compareLocal(now, r.start_local) < 0;

/**
 * Whether to ask "how many people": not when the amenity holds one person
 * at a time, where the only answer is 1.
 */
export const showPartySize = (a: Pick<AmenityReturn, 'max_party_size'>): boolean =>
  a.max_party_size === null || a.max_party_size === undefined || a.max_party_size > 1;

/**
 * Who ended a cancelled or expired booking, said to the household. Null
 * when it is neither, or when the server did not say.
 */
export const endedByText = (
  r: Pick<ReservationReturn, 'status' | 'cancelled_by_kind'>
): string | null => {
  if (r.status === 'expired') {
    return 'Expired — no one acted on the request in time, so the time was released.';
  }
  if (r.status !== 'cancelled') return null;
  switch (r.cancelled_by_kind) {
    case 'household':
      return 'Cancelled by your household.';
    case 'management':
      return 'Cancelled by management.';
    case 'system':
      return 'Cancelled automatically.';
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Moving a booking, and taking it to a calendar (#640 phase 2)
// ---------------------------------------------------------------------------

/**
 * Whether the household may move the booking itself: active, not started,
 * and — the backend's rule — before the cancellation deadline, after which
 * a move would be a way round the late-cancellation charge. The deadline
 * is `fee_refundable_until_local` when the booking has a fee; a free
 * booking can be moved until it starts. The server decides; this only
 * decides whether to offer the button.
 */
export const canReschedule = (
  r: Pick<ReservationReturn, 'status' | 'start_local' | 'fee_refundable_until_local'>,
  now: string
): boolean => {
  if (r.status !== 'pending' && r.status !== 'approved') return false;
  if (compareLocal(now, r.start_local) >= 0) return false;
  const until = r.fee_refundable_until_local;
  return !until || compareLocal(now, until) < 0;
};

/** `$150.00 → $200.00`, or `$150.00 (unchanged)`. */
export const feeChangeText = (before: string, after: string): string => {
  const label = (amount: string): string => (isFree(amount) ? 'Free' : formatMoney(amount));
  if (compareMoney(before, after) === 0) return `${label(after)} (unchanged)`;
  return `${label(before)} → ${label(after)}`;
};

/**
 * What moving a booking does to its fee, from the reschedule preview's
 * `fee_outcome` (#640 phase 2). Compared as strings, not through the
 * generated const: that is a runtime import of the client, which pulls in
 * axios as ESM and Jest cannot load it.
 */
export const moveFeeOutcomeText = (
  outcome: ReservationMoveFeeOutcome,
  change: ReservationFeeChange
): string => {
  const amounts = feeChangeText(change.from, change.to);
  switch (outcome) {
    case 'unchanged':
      return isFree(change.to) ? 'Free' : `${amounts}.`;
    case 'requoted':
      return `${amounts}. Nothing is charged until the request is approved.`;
    case 'reposted':
      return `${amounts}. The old charge comes off your account and the new one goes on when you confirm.`;
    case 'returned':
      return `${amounts}. You've paid already; the difference is returned to you.`;
    case 'kept_paid':
      return `${amounts}. You've paid already, and that payment stands.`;
    case 'refused_fee_paid':
    default:
      return `${amounts}. You've already paid for this booking, so a move that changes the price has to go through your management company.`;
  }
};

/** A file name for a booking's calendar entry: `clubhouse-2026-10-03.ics`. */
export const calendarFileName = (
  r: Pick<ReservationReturn, 'amenity_name' | 'local_date'>
): string => {
  const slug = r.amenity_name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'reservation'}-${r.local_date}.ics`;
};
